/**
 * MODULE: domain/pages/frontend/components/editor/pagesEditorExtensions.ts
 * PURPOSE: TipTap extensions for the Pages note editor
 */

import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import { Node, mergeAttributes } from '@tiptap/core';
import NodeRange from '@tiptap/extension-node-range';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { ReactNodeViewRenderer } from '@tiptap/react';
import type { Doc } from 'yjs';
import type { WebsocketProvider } from 'y-websocket';
import { PagesAiBlockView } from './PagesAiBlockView';
import { PagesCalloutBlockView } from './PagesCalloutBlockView';
import { PagesCrmMentionView } from './PagesCrmMentionView';
import { PagesFileBlockView } from './PagesFileBlockView';
import { PagesImageBlockView } from './PagesImageBlockView';
import { PagesPageLinkBlockView } from './PagesPageLinkBlockView';
import { PagesBookmarkBlockView } from './PagesBookmarkBlockView';
import { PagesActionButtonBlockView } from './PagesActionButtonBlockView';
import { PagesDatabaseEmbedBlockView } from './PagesDatabaseEmbedBlockView';
import { PagesPipelineSnapshotBlockView } from './PagesPipelineSnapshotBlockView';
import { PagesCrmDealPreviewBlockView } from './PagesCrmDealPreviewBlockView';
import { PagesCrmContactPreviewBlockView } from './PagesCrmContactPreviewBlockView';
import { PagesCrmCompanyPreviewBlockView } from './PagesCrmCompanyPreviewBlockView';
import { PagesTocBlockView } from './PagesTocBlockView';
import { PagesTasksSyncBlockView } from './PagesTasksSyncBlockView';
import { PagesTranscriptPreviewBlockView } from './PagesTranscriptPreviewBlockView';
import { PagesToggleBlockView } from './PagesToggleBlockView';
import { PagesTodoBlockView } from './PagesTodoBlockView';
import { PagesBlockMeta } from './pagesBlockMeta';

export const PagesImageBlock = Node.create({
  name: 'pagesImageBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      fileId: { default: null },
      url: { default: null },
      filename: { default: null },
      mimeType: { default: null },
      size: { default: 0 },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-image-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-image-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesImageBlockView);
  },
});

export const PagesFileBlock = Node.create({
  name: 'pagesFileBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      fileId: { default: null },
      url: { default: null },
      filename: { default: null },
      mimeType: { default: null },
      size: { default: 0 },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-file-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-file-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesFileBlockView);
  },
});

export const PagesAiBlock = Node.create({
  name: 'pagesAiBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      pageId: { default: null },
      preset: { default: 'summarise' },
      prompt: { default: '' },
      response: { default: '' },
      status: { default: 'loading' },
      model: { default: null },
      errorMessage: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-ai-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-ai-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesAiBlockView);
  },
});

export const PagesCrmMention = Node.create({
  name: 'crmMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      id: { default: null },
      entityType: { default: 'contact' },
      label: { default: '' },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-crm-mention]' }];
  },
  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-crm-mention': '',
        'data-entity-type': node.attrs.entityType,
      }),
      `@${node.attrs.label}`,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesCrmMentionView, { as: 'span' });
  },
});

export const PagesPageLinkBlock = Node.create({
  name: 'pagesPageLinkBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      pageId: { default: null },
      workspaceId: { default: null },
      title: { default: 'Untitled' },
      icon: { default: null },
      pageType: { default: 'note' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-page-link-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-page-link-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesPageLinkBlockView);
  },
});

export const PagesBookmarkBlock = Node.create({
  name: 'pagesBookmarkBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      url: { default: '' },
      title: { default: 'Bookmark' },
      description: { default: '' },
      imageUrl: { default: null },
      faviconUrl: { default: null },
      display: { default: 'card' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-bookmark-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-bookmark-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesBookmarkBlockView);
  },
});

export const PagesActionButtonBlock = Node.create({
  name: 'pagesActionButtonBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      label: { default: 'Get started' },
      linkType: { default: 'external' },
      url: { default: '' },
      pageId: { default: null },
      workspaceId: { default: null },
      pageTitle: { default: null },
      variant: { default: 'primary' },
      openInNewTab: { default: true },
      hostPageId: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-action-button-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-action-button-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesActionButtonBlockView);
  },
});

export const PagesDatabaseEmbedBlock = Node.create({
  name: 'pagesDatabaseEmbedBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      databasePageId: { default: null },
      workspaceId: { default: null },
      databaseTitle: { default: 'Untitled database' },
      databaseIcon: { default: null },
      maxRows: { default: 10 },
      searchFilter: { default: '' },
      hostPageId: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-database-embed-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-database-embed-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesDatabaseEmbedBlockView);
  },
});

export const PagesPipelineSnapshotBlock = Node.create({
  name: 'pagesPipelineSnapshotBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      title: { default: 'Pipeline snapshot' },
      showKpis: { default: true },
      showFunnel: { default: true },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-pipeline-snapshot-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-pipeline-snapshot-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesPipelineSnapshotBlockView);
  },
});

export const PagesCrmDealPreviewBlock = Node.create({
  name: 'pagesCrmDealPreviewBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      dealId: { default: null },
      dealLabel: { default: 'Untitled deal' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-crm-deal-preview-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-crm-deal-preview-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesCrmDealPreviewBlockView);
  },
});

export const PagesCrmContactPreviewBlock = Node.create({
  name: 'pagesCrmContactPreviewBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      contactId: { default: null },
      contactLabel: { default: 'Untitled contact' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-crm-contact-preview-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-crm-contact-preview-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesCrmContactPreviewBlockView);
  },
});

export const PagesCrmCompanyPreviewBlock = Node.create({
  name: 'pagesCrmCompanyPreviewBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      companyId: { default: null },
      companyLabel: { default: 'Untitled account' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-crm-company-preview-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-crm-company-preview-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesCrmCompanyPreviewBlockView);
  },
});

export const PagesToggleBlock = Node.create({
  name: 'pagesToggleBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      title: { default: 'Toggle' },
      body: { default: '' },
      open: { default: true },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-toggle-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-toggle-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesToggleBlockView);
  },
});

export const PagesCalloutBlock = Node.create({
  name: 'pagesCalloutBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      text: { default: '' },
      variant: { default: 'info' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-callout-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-callout-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesCalloutBlockView);
  },
});

export const PagesTocBlock = Node.create({
  name: 'pagesTocBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      title: { default: 'On this page' },
      maxDepth: { default: 3 },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-toc-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-toc-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesTocBlockView);
  },
});

export const PagesTasksSyncBlock = Node.create({
  name: 'pagesTasksSyncBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      pageId: { default: null },
      title: { default: 'Tasks' },
      showCompleted: { default: false },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-tasks-sync-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-tasks-sync-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesTasksSyncBlockView);
  },
});

export const PagesTranscriptPreviewBlock = Node.create({
  name: 'pagesTranscriptPreviewBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      callId: { default: null },
      callLabel: { default: 'Recorded call' },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-transcript-preview-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-transcript-preview-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesTranscriptPreviewBlockView);
  },
});

export const PagesTodoBlock = Node.create({
  name: 'pagesTodoBlock',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      text: { default: '' },
      checked: { default: false },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-pages-todo-block]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-pages-todo-block': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(PagesTodoBlockView);
  },
});

export type PagesEditorCollaborationOptions = {
  ydoc: Doc;
  provider: WebsocketProvider | null;
  user: { name: string; color: string };
};

export function buildPagesEditorExtensions(collaboration?: PagesEditorCollaborationOptions | null) {
  const collaborationEnabled = Boolean(collaboration?.ydoc);
  const extensions = [
    StarterKit.configure({
      ...(collaborationEnabled ? { history: false as const } : {}),
    }),
    NodeRange,
    PagesBlockMeta,
    PagesImageBlock,
    PagesFileBlock,
    PagesPageLinkBlock,
    PagesBookmarkBlock,
    PagesActionButtonBlock,
    PagesDatabaseEmbedBlock,
    PagesPipelineSnapshotBlock,
    PagesCrmDealPreviewBlock,
    PagesCrmContactPreviewBlock,
    PagesCrmCompanyPreviewBlock,
    PagesToggleBlock,
    PagesCalloutBlock,
    PagesTocBlock,
    PagesTasksSyncBlock,
    PagesTranscriptPreviewBlock,
    PagesTodoBlock,
    PagesAiBlock,
    PagesCrmMention,
    Placeholder.configure({
      placeholder: "Start writing, or type '/' for commands…",
      emptyEditorClass: 'is-editor-empty',
      emptyNodeClass: 'is-editor-empty',
    }),
  ];

  if (collaboration?.ydoc) {
    extensions.push(
      Collaboration.configure({
        document: collaboration.ydoc,
      }),
      CollaborationCursor.configure({
        provider: collaboration.provider ?? undefined,
        user: collaboration.user,
      })
    );
  }

  return extensions;
}
